import { createFileRoute, Link } from "@tanstack/react-router";
import { Shield, ArrowRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { PageGallery } from "@/components/PageGallery";
import type { PoliceClearanceCountry } from "@/content/police-clearance/registry";
import { PC_PROVINCE_LANDINGS } from "@/content/police-clearance-provinces";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

import { buildFaqSchema } from "@/lib/schema";

export const Route = createFileRoute("/police-clearance/")({
  loader: async (): Promise<{
    PC_KEYWORDS_TH: string[];
    PC_FAQ_TH: { q: string; a: string }[];
    countries: PoliceClearanceCountry[];
  }> => {
    const [m0, m1] = await Promise.all([
      import("@/content/police-clearance-corpus"),
      import("@/content/police-clearance/registry"),
    ]);
    return {
      PC_KEYWORDS_TH: m0.PC_KEYWORDS_TH,
      PC_FAQ_TH: m0.PC_FAQ_TH_FULL,
      countries: m1.POLICE_CLEARANCE_COUNTRIES,
    };
  },
  head: ({ loaderData }) => {
    const { PC_KEYWORDS_TH = [], PC_FAQ_TH = [], countries = [] } = loaderData ?? {};

    const site = getActiveSiteUrl();
    const base = pageHeadForProvince({
      title: "ตรวจประวัติอาชญากรรมต่างประเทศ — FBI / ACRO / AFP / RCMP",
      description:
        "ขอใบตรวจประวัติอาชญากรรมจาก 8 ประเทศ (FBI, ACRO, AFP, RCMP, NZ MoJ, Singapore CoC, Japan, Korea) จากภูเก็ต ครบ Apostille + แปล + MFA ในจุดเดียว",
      path: "/police-clearance",
      keywords: PC_KEYWORDS_TH.slice(0, 80),
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "Police Clearance", url: `${site}/police-clearance` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Police Clearance Certificates from Phuket",
            numberOfItems: countries.length,
            itemListElement: countries.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: `${c.certName} — ${c.countryEn}`,
              url: `${site}/police-clearance/${c.slug}`,
            })),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(PC_FAQ_TH.slice(0, 15))) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "#ai-answer-tldr"] },
            keywords: PC_KEYWORDS_TH.slice(0, 100).join(", "),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/police-clearance")) },
      ],
    };
  },
  component: PoliceClearanceIndex,
});

function PoliceClearanceIndex() {
  const { PC_KEYWORDS_TH, PC_FAQ_TH, countries } = Route.useLoaderData() as { PC_KEYWORDS_TH: string[]; PC_FAQ_TH: { q: string; a: string }[]; countries: PoliceClearanceCountry[] };
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="text-xs font-bold uppercase tracking-widest text-gold">
              <Shield className="mr-1 inline h-3.5 w-3.5" /> Police Clearance — International
            </div>
            <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">
              ตรวจประวัติอาชญากรรมต่างประเทศ จากภูเก็ต
            </h1>
            <p id="ai-answer-tldr" className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
              บริการขอใบตรวจประวัติอาชญากรรม (Police Clearance Certificate) จาก 8 ประเทศหลัก —
              FBI (US), ACRO (UK), AFP (AU), RCMP (CA), MoJ (NZ), CoC (SG), 犯罪経歴 (JP),
              범죄경력 (KR) — ครบบริการพิมพ์ลายนิ้วมือในภูเก็ต, Apostille, แปลรับรอง, และ MFA ในจุดเดียว
              ไม่ต้องบินกลับประเทศต้นทาง
            </p>
          </div>
        </section>

        <section>
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">เลือกประเทศที่ต้องการตรวจประวัติ</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {countries.map((c: PoliceClearanceCountry) => (
                <Link
                  key={c.slug}
                  to="/police-clearance/$country"
                  params={{ country: c.slug }}
                  className="group rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-gold"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-2xl">{c.flag}</div>
                      <div className="mt-1 font-bold text-primary">{c.certName}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {c.authorityShort} · {c.countryTh}
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-gold transition-transform group-hover:translate-x-1" />
                  </div>
                  <div className="mt-3 text-sm text-muted-foreground">{c.aeoAnswer.slice(0, 140)}…</div>
                  <div className="mt-3 text-xs font-bold text-gold">
                    เริ่ม ฿{c.priceTHB.from.toLocaleString()}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">บริการตามจังหวัด</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              ให้บริการพิมพ์ลายนิ้วมือและขอใบตรวจประวัติในจังหวัดหลักทั่วไทย ไม่ต้องบินกรุงเทพ
            </p>
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              {PC_PROVINCE_LANDINGS.map((p) => (
                <Link
                  key={p.slug}
                  to="/police-clearance/province/$province"
                  params={{ province: p.slug }}
                  className="rounded-xl border border-border bg-card p-4 text-sm transition-all hover:-translate-y-0.5 hover:border-gold"
                >
                  <div className="font-bold text-primary">{p.provinceTh}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{p.region}</div>
                </Link>
              ))}
            </div>
            <div className="mt-6 rounded-xl border border-gold/30 bg-gold/5 p-4 text-sm">
              🇬🇧 English readers: see the <Link to="/en/police-clearance/faq" className="font-bold text-gold underline">English Police Clearance FAQ Hub</Link> for FBI, ACRO, AFP, RCMP guides.
            </div>
          </div>
        </section>
        <section className="container mx-auto max-w-5xl px-4 pb-12">
          <PageGallery category="background" seed="pc-hub" locale="th" limit={4} />
          <FaqSourceNote topic="police-clearance" locale="th" />
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}